import { ReactElement, useMemo, useRef, useState } from 'react';
import { Alert, Modal, ModalFooter } from '@sc/shared-ui';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { notify } from '../utility/notify';
import env from '../environment';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';
import { NewCardForm, NewCardFormHandle } from '../components/NewCardForm';
import {
  GQL_MUTATION_CREATE_SETUP_INTENT,
  GQL_MUTATION_SAVE_PAYMENT_METHOD,
  GQL_QUERY_LIST_PAYMENT_METHODS,
} from '../utility/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  // Allocate the new card to the monthly plan on save (also syncs it as the
  // Stripe subscription default server-side).
  useForMonthlyPlan?: boolean;
  // Runs after the card is saved, before the modal closes — e.g. resume a
  // subscription that was paused for want of a card.
  onSuccess?: () => void;
}

const stripePromise = env.stripePublishableKey
  ? loadStripe(env.stripePublishableKey)
  : null;

const AddPaymentMethodForm = ({
  onClose,
  loading,
  setLoading,
  useForMonthlyPlan,
  onSuccess,
}: {
  onClose: () => void;
  loading: boolean;
  setLoading: (next: boolean) => void;
  useForMonthlyPlan?: boolean | undefined;
  onSuccess?: (() => void) | undefined;
}): ReactElement => {
  const stripe = useStripe();
  const client = useApolloClient();
  const { user } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const newCardRef = useRef<NewCardFormHandle>(null);

  const [createSetupIntent] = useMutation(GQL_MUTATION_CREATE_SETUP_INTENT, {
    onError,
  });

  const [savePaymentMethod] = useMutation(GQL_MUTATION_SAVE_PAYMENT_METHOD, {
    onError,
  });

  const submit = async () => {
    if (!stripe) return;

    setLoading(true);

    // Wrapped in try/finally so `loading` is always released — the modal
    // blocks closing while loading, so a stuck flag would trap the user.
    try {
      const { data } = await createSetupIntent();
      const clientSecret = data?.createSetupIntent.clientSecret;

      if (!clientSecret) return;

      const result = await newCardRef.current?.confirm(clientSecret);

      if (!result?.paymentMethodId) {
        if (result?.error) notify.error(result.error);
        return;
      }

      // Persist the card locally so we can track its allocation flags. Null
      // allocations leave it to the server, which auto-allocates the first
      // card on the company to every use.
      const { data: saved } = await savePaymentMethod({
        variables: {
          input: {
            paymentMethodId: result.paymentMethodId,
            paymentMethodUuid: null,
            useForMonthlyPlan: useForMonthlyPlan ? true : null,
            billingDetails: null,
          },
        },
      });

      if (!saved?.savePaymentMethod) return;

      // confirmCardSetup attaches the card on Stripe's side; refetch the list
      // now so the new card (and its allocation flags) show up. Refetching on
      // the createSetupIntent mutation would have fired before the attach
      // completed.
      await client.refetchQueries({
        include: [GQL_QUERY_LIST_PAYMENT_METHODS],
      });

      notify.success('Payment method added.');

      onSuccess?.();

      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        void submit();
      }}
    >
      <div className="p-5">
        <NewCardForm ref={newCardRef} defaultName={user.name ?? ''} />
      </div>
      <ModalFooter
        submitText="Save card"
        asForm
        onClose={onClose}
        buttonsDisabled={loading || !stripe}
      />
    </form>
  );
};

export const AddPaymentMethodModal = ({
  open,
  onClose,
  useForMonthlyPlan,
  onSuccess,
}: Props): ReactElement => {
  const elementsKey = useMemo(() => (open ? Date.now() : 0), [open]);
  const [loading, setLoading] = useState(false);

  return (
    <Modal
      title="Add payment method"
      open={open}
      onClose={loading ? undefined : onClose}
      dialogPanelClassname="w-150"
      loading={loading}
    >
      {stripePromise ? (
        <Elements
          key={elementsKey}
          stripe={stripePromise}
          options={{ appearance: { theme: 'night' } }}
        >
          <AddPaymentMethodForm
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
            useForMonthlyPlan={useForMonthlyPlan}
            onSuccess={onSuccess}
          />
        </Elements>
      ) : (
        <div className="p-5">
          <Alert type="serious" message="Something went wrong." />
        </div>
      )}
    </Modal>
  );
};
